import {
    useCallback,
    useEffect,
    useRef
} from 'react';

import { useSocketStore } from '../../stores/useSocketStore';
import { useMapStore } from '../../stores/map/useMapStore';

export function usePezinhoSocket() {

    const previousMapRef = useRef(null);
    const firstSearchEffectRef = useRef(true);

    const socket = useSocketStore(
        state => state.socket
    );

    const openMap = useMapStore(
        state => state.openMap
    );

    const pezinhoActive = useMapStore(
        state => state.pezinho.active
    );

    const selectedPoint = useMapStore(
        state => state.selectedPoint
    );

    const codigoMap = useMapStore(
        state => state.codigoMap
    );

    const tripUidMap = useMapStore(
        state => state.tripUidMap
    );

    // =========================
    // SEARCH
    // =========================

    const searchPezinho = useCallback(() => {

        if (
            !socket ||
            !socket.connected
        ) {
            return;
        }

        const {
            setPezinhoRequestId,
            openMap,
            selectedPoint,
            pezinho,
            setPezinhoLoading,
            clearPezinhoResults
        } = useMapStore.getState();

        if (
            !openMap ||
            !pezinho.active ||
            !selectedPoint ||
            !pezinho.startAt ||
            !pezinho.endAt ||
            pezinho.loading
        ) {
            return;
        }

        const pontoReferencia =
            selectedPoint?.pontoReferencia;

        const lat = Number(
            pontoReferencia?.LATITUDE
        );

        const lng = Number(
            pontoReferencia?.LONGITUDE
        );

        const raio =
            Number(
                pontoReferencia?.RAIO
            ) || 300;

        if (
            !Number.isFinite(lat) ||
            !Number.isFinite(lng)
        ) {
            return;
        }

        const requestId = crypto.randomUUID();

        // =========================
        // START
        // =========================

        setPezinhoRequestId(requestId);
        clearPezinhoResults();
        setPezinhoLoading(true);

        socket.emit(
            'pezinho:search',
            {
                requestId,
                lat,
                lng,
                raio,
                startAt: pezinho.startAt,
                endAt: pezinho.endAt
            },
            response => {

                const {
                    openMap,
                    pezinho,
                    setPezinhoLoading,
                    setPezinhoVehicles,
                    setPezinhoRequestId
                } = useMapStore.getState();

                // =========================
                // REQUEST INVÁLIDA
                // =========================

                if (
                    response?.requestId !== requestId ||
                    pezinho.requestId !== requestId
                ) {
                    return;
                }

                // =========================
                // MAPA / PEZINHO FECHADO
                // =========================

                if (
                    !openMap ||
                    !pezinho.active
                ) {
                    return;
                }

                // =========================
                // FINALIZA REQUEST
                // =========================

                setPezinhoRequestId(null);
                setPezinhoLoading(false);

                // =========================
                // ERRO
                // =========================

                if (!response?.ok) {

                    setPezinhoVehicles([]);

                    return;
                }

                // =========================
                // RESULTADO
                // =========================

                setPezinhoVehicles(
                    response?.data?.vehicles || []
                );

            }
        );

    }, [socket]);

    // =========================
    // MAP CONTENT CHANGE
    // =========================

    useEffect(() => {

        const mapKey =
            `${codigoMap || ''}:${tripUidMap || ''}`;

        // =========================
        // PRIMEIRA CARGA
        // =========================

        if (previousMapRef.current === null) {

            previousMapRef.current =
                mapKey;

            return;
        }

        // =========================
        // MESMO MAPA
        // =========================

        if (
            previousMapRef.current ===
            mapKey
        ) {
            return;
        }

        previousMapRef.current =
            mapKey;

        // =========================
        // RESET PEZINHO
        // =========================

        const {
            clearPezinho
        } = useMapStore.getState();

        clearPezinho();

    }, [
        codigoMap,
        tripUidMap
    ]);

    // =========================
    // DISCONNECT
    // =========================

    useEffect(() => {

        if (!socket) {
            return;
        }

        const handleDisconnect = () => {

            const {
                setPezinhoLoading,
                setPezinhoRequestId
            } = useMapStore.getState();

            setPezinhoRequestId(null);
            setPezinhoLoading(false);

        };

        socket.on('disconnect', handleDisconnect);

        return () => {

            socket.off('disconnect', handleDisconnect);

        };

    }, [socket]);

    // =========================
    // ACTIVE / POINT CHANGE
    // =========================

    useEffect(() => {

        const {
            pezinho,
            resetPezinhoSearch
        } = useMapStore.getState();

        // =========================
        // SEM PONTO / DESATIVADO
        // =========================

        if (
            !selectedPoint ||
            !pezinhoActive
        ) {

            resetPezinhoSearch();

            return;
        }

        // =========================
        // MAPA FECHADO
        // =========================

        if (!openMap) {
            return;
        }

        // =========================
        // PRIMEIRA EXECUÇÃO
        // =========================

        if (firstSearchEffectRef.current) {

            firstSearchEffectRef.current = false;

            // estado já veio sincronizado
            if (pezinho.loaded) {
                return;
            }

        }

        // =========================
        // BUSCA
        // =========================

        searchPezinho();

    }, [
        openMap,
        pezinhoActive,
        selectedPoint,
        searchPezinho
    ]);

    return {
        searchPezinho
    };
}